import type { ContractDocument } from "./contractContent";

/**
 * BELGE GÖRÜNTÜLEME VE İNDİRME (CLOS-120 `[101]`).
 *
 * ### ⚠ Ön izleme kaynağın YERİNE GEÇMİYOR
 *
 * Kart: *"ön izleme türevi kaynak dosyanın yerine geçmez ve kaynak hash'ini değiştirmez."*
 * Bu yüzden görünümde ikisi AYRI alan: ekranda okunan metin `[098]`'in türevidir, indirilen
 * dosya `[133]`'ün değişmez versiyonuna bağlı kaynaktır. Tek alanda birleştirseydik
 * kullanıcı ekranda gördüğü metnin imzalanacak metin olduğunu sanardı.
 *
 * ### ⚠ Depolama adresi burada YOK
 *
 * `[090]`'dan beri taşınan kural: görünüm tipi gizli depolama adresi taşımıyor. İndirme
 * bağlantısı ayrı bir çağrıyla, o an yeniden verilen bir erişim kararıyla üretiliyor.
 */

/**
 * Metin çıkarımının sonucu.
 *
 * Dördü de AYRI durum: `ocr_required` bozuk bir belge değil, metin katmanı olmayan bir
 * belgedir; `unsupported_type` ise hiç denenmemiş demektir. Tek bir "başarısız" durumuna
 * indirseydik kullanıcıya ne yapması gerektiğini söyleyemezdik.
 */
export const EXTRACTION_STATUSES = [
  "extracted",
  "ocr_required",
  "unreadable",
  "unsupported_type",
] as const;
export type ExtractionStatus = (typeof EXTRACTION_STATUSES)[number];

/** Çıkarım hiç koşmadıysa `pending` — "yok" ile "başarısız" aynı şey değil. */
export type PreviewExtractionStatus = ExtractionStatus | "pending";

export const PREVIEW_EXTRACTION_STATUS_NAMES: Record<PreviewExtractionStatus, string> = {
  pending: "Ön izleme hazırlanıyor",
  extracted: "Ön izleme hazır",
  ocr_required: "Belgede metin katmanı yok",
  unreadable: "Belge okunamadı",
  unsupported_type: "Bu dosya biçimi ön izlenemiyor",
};

/**
 * Ön izleme gösterilemediğinde kullanıcıya SÖYLENEN şey.
 *
 * Kart: *"ön izleme başarısızsa kaynak dosya veya güvenli hata seçeneği sunulur."* Metin
 * şemada duruyor; ekran kendi cümlesini uydurmuyor (refleks 4).
 */
export const PREVIEW_FALLBACK_NOTES: Record<
  Exclude<PreviewExtractionStatus, "extracted">,
  string
> = {
  pending: "Belgenin metni henüz hazırlanmadı. Kaynak dosyayı indirerek görüntüleyebilirsiniz.",
  ocr_required:
    "Bu belge taranmış görüntülerden oluşuyor; uygulama içinde okunabilir metni yok. Kaynak dosyayı indirerek görüntüleyebilirsiniz.",
  unreadable:
    "Belgenin metni çıkarılamadı. Kaynak dosya değişmeden duruyor; indirerek görüntüleyebilirsiniz.",
  unsupported_type:
    "Bu dosya biçimi uygulama içinde ön izlenemiyor. Kaynak dosyayı indirerek görüntüleyebilirsiniz.",
};

export type ContractVersionPreviewView = {
  versionId: string;
  versionNumber: number;
  extraction: {
    status: PreviewExtractionStatus;
    document: ContractDocument | null;
    pageCount: number | null;
    characterCount: number;
    losses: string[];
    /** Kullanıcıya gösterilecek Türkçe karşılıklar — UI metin uydurmuyor (refleks 4). */
    lossNames: string[];
  };
  ocr: {
    status: string;
    pageCount: number | null;
    averageConfidence: number | null;
    lowConfidenceCount: number;
  } | null;
  source: {
    fileName: string | null;
    contentType: string | null;
    sizeBytes: number | null;
    /** Kaynak içerik hâlâ duruyor mu. */
    available: boolean;
  };
  /** Kaynak belge görüntülenebilir mi — OCR'dan bağımsız. */
  sourceViewable: boolean;
  /** Metin araması ve metin tabanlı karşılaştırma mümkün mü. */
  searchableText: boolean;
};

/**
 * İNDİRME BİLETİ.
 *
 * ⚠ Bilet KISA ÖMÜRLÜ ve saklanmıyor. Kart: *"kaynak kayıt, versiyon veya erişim durumu
 * değiştiğinde ESKİ SÜRELİ URL yeni erişim sağlamaz."* Bağlantıyı önbelleğe alan bir
 * istemci, erişimi kaldırılmış bir kullanıcıya çalışan bir adres bırakırdı.
 */
export type ContractDocumentDownloadView = {
  downloadUrl: string;
  expiresInSeconds: number;
  fileName: string | null;
};

/**
 * VERSİYONUN ARTIFACT SETİ (CLOS-1236 — Kanon §6, §7).
 *
 * ⚠ Ekran hangi indirme düğmesini göstereceğini BURADAN öğreniyor, tahminle değil.
 * "Versiyon varsa canonical de vardır" diye varsaysaydık, CLOS-1232 öncesi dondurulmuş
 * versiyonlarda düğme çalışmayan bir indirmeye götürürdü.
 *
 * ⚠ `sha256` kullanıcıya da gösteriliyor: canonical'ın hash'i sözleşmenin hukuki
 * kimliğinin parçası (§17) ve karşı tarafla teyit edilebilir olmalı.
 */
export const CONTRACT_ARTIFACT_TYPES = ["canonical", "redline", "materialized"] as const;
export type ContractArtifactType = (typeof CONTRACT_ARTIFACT_TYPES)[number];

export const CONTRACT_ARTIFACT_LABELS: Record<ContractArtifactType, string> = {
  /** ⚠ Kullanıcıya "canonical" denmiyor: kanon §28.2 teknik kavram göstermeyi yasaklıyor. */
  canonical: "Temiz Sürüm",
  redline: "Değişiklikleri Göster",
  /**
   * ⚠ "Materyalize" DENMİYOR (§28.2). Ama "Temiz Sürüm" de denmiyor: bu dosya versiyonun
   * formal hukuki hedefi DEĞİL, jsonb metninden üretilmiş bir TEMSİL. Adının bu farkı
   * söylemesi gerekiyor, yoksa kullanıcı imzalanacak belge sanır.
   */
  materialized: "Word Belgesi (metinden üretildi)",
};

export type ContractVersionArtifactView = {
  artifactType: ContractArtifactType;
  sha256: string;
  fileName: string | null;
  sizeBytes: number | null;
  createdAt: string;
};

/** İnsan okunur dosya boyutu; ham bayt sayısı kullanıcıya bir şey anlatmıyor. */
export function formatFileSize(bytes: number | null): string {
  if (bytes === null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const birimler = ["KB", "MB", "GB"];
  let deger = bytes / 1024;
  let i = 0;
  while (deger >= 1024 && i < birimler.length - 1) {
    deger /= 1024;
    i += 1;
  }
  return `${deger.toFixed(deger < 10 ? 1 : 0)} ${birimler[i]}`;
}
